import { useState } from "react";
import { actionKindSchema, type ReadClientPlanItem } from "@ferrivia/api-contract";
import { useTracker } from "@ferrivia/auth-web";
import { useTranslation } from "@ferrivia/i18n";
import { Card, Text } from "@ferrivia/ui";
import { ChevronRight } from "lucide-react";
import { usePlan } from "../../../../hooks/usePlan";
import { PlanItemSheet } from "./PlanItemSheet";
import { kindIcon, usePlanCopy } from "./usePlanCopy";
import s from "./PlanBlock.module.css";

/**
 * "Suunnitelmasi": the actions the care team marked "shown in app", in the
 * server's order (action-plan.ts, human-set dates only). Every title and
 * instruction is the clinician's text, verbatim; this component orders,
 * selects and hides nothing. The block carries no payload — it fetches its
 * own items so care text never rides on the home surface.
 */
export function PlanBlock() {
  const { t } = useTranslation("client");
  const tracker = useTracker();
  const plan = usePlan();
  const { authorLine, dateLine } = usePlanCopy();
  const [open, setOpen] = useState<ReadClientPlanItem | null>(null);
  const [shown, setShown] = useState<ReadClientPlanItem | null>(null);

  if (plan.status !== "success" || plan.items.length === 0) {
    return null;
  }

  const openItem = (item: ReadClientPlanItem) => {
    const kind = actionKindSchema.safeParse(item.kind);
    if (kind.success) {
      tracker.track("plan.item_opened", { kind: kind.data, surface: "home" });
    }
    setShown(item);
    setOpen(item);
  };

  return (
    <Card role="region" className={s.root} aria-labelledby="plan-heading">
      <Text as="h2" size="lg" weight="bold" id="plan-heading" className={s.heading}>
        {t("home.blocks.plan.heading", "Suunnitelmasi")}
      </Text>
      <ul className={s.list}>
        {plan.items.map((item) => {
          const Icon = kindIcon(item.kind);
          const date = dateLine(item);
          return (
            <li key={item.id} className={s.item}>
              <button type="button" className={s.row} onClick={() => openItem(item)}>
                <span className={s.tile}>
                  <Icon aria-hidden size={20} />
                </span>
                <span className={s.text}>
                  <span className={s.title}>{item.title}</span>
                  <span className={s.meta}>{authorLine(item)}</span>
                  {date !== null && <span className={s.meta}>{date}</span>}
                </span>
                <ChevronRight aria-hidden size={20} className={s.chevron} />
              </button>
            </li>
          );
        })}
      </ul>
      <PlanItemSheet item={open} shown={shown} onClose={() => setOpen(null)} />
    </Card>
  );
}
